/**
 * Sales Inbox — list + detail view for inbound emails processed by the
 * Titan IMAP agent. Users can see classification, linked prospect/site,
 * attachments, and approve/send the generated draft reply.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Inbox, Paperclip, ChevronRight, Send, Sparkles, Loader2, ExternalLink,
  RefreshCw, UserPlus, Building2, FileText, Mail, CheckCircle, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Sidebar } from '../components/Sidebar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ Authorization: 'Bearer ' + (localStorage.getItem('token') || localStorage.getItem('access_token')) });

const CLASS_STYLE = {
  internal_instruction: { label: 'Internal · Instruction', bg: 'bg-violet-100 text-violet-800' },
  internal_followup:    { label: 'Internal · Follow-up',   bg: 'bg-slate-100 text-slate-700' },
  external_lead:        { label: 'External · Lead',        bg: 'bg-emerald-100 text-emerald-800' },
};
const STATUS_STYLE = {
  received:     'bg-slate-100 text-slate-700',
  processed:    'bg-blue-50 text-blue-800',
  needs_review: 'bg-amber-50 text-amber-800',
  replied:      'bg-emerald-50 text-emerald-800',
  failed:       'bg-red-50 text-red-800',
};

export default function SalesInboxPage() {
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draftEdit, setDraftEdit] = useState({ subject: '', body_html: '' });
  const [classFilter, setClassFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = classFilter ? `?classification=${classFilter}` : '';
      const { data } = await axios.get(`${API}/sales-inbox${q}`, { headers: AUTH() });
      setRows(data.rows || []);
    } catch (e) { toast.error('Failed to load inbox'); }
    setLoading(false);
  }, [classFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const { data } = await axios.get(`${API}/sales-inbox/${id}`, { headers: AUTH() });
      setDetail(data);
      setDraftEdit({
        subject: data.draft?.subject || '',
        body_html: data.draft?.body_html || '',
      });
    } catch (e) { toast.error('Failed to load message'); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      await axios.post(`${API}/sales-inbox/poll-now`, {}, { headers: AUTH() });
      toast.success('Polled Titan inbox');
      await load();
    } catch (e) { toast.error('Poll failed'); }
    setPolling(false);
  };

  const saveDraft = async () => {
    if (!detail?.draft) return;
    try {
      await axios.put(`${API}/sales-inbox/draft`, {
        draft_id: detail.draft.draft_id,
        subject: draftEdit.subject,
        body_html: draftEdit.body_html,
      }, { headers: AUTH() });
      toast.success('Draft saved');
    } catch (e) { toast.error('Save failed'); }
  };

  const sendReply = async () => {
    if (!detail?.draft) return;
    setSending(true);
    try {
      await saveDraft();
      await axios.post(`${API}/sales-inbox/send-reply`, { draft_id: detail.draft.draft_id }, { headers: AUTH() });
      toast.success('Reply sent via Titan SMTP');
      await openDetail(selectedId);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const generateProposal = async () => {
    if (!detail?.message) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/sales-inbox/generate-proposal`,
        { inbox_message_id: detail.message.inbox_message_id }, { headers: AUTH() });
      if (data.proposal_preview_url) {
        toast.success('Proposal draft generated');
        window.open(data.proposal_preview_url, '_blank');
      } else {
        toast.warning('Could not generate — check linked prospect/site');
      }
      await openDetail(selectedId);
    } catch (e) { toast.error('Generation failed'); }
    setGenerating(false);
  };

  return (
    <div className="flex min-h-screen bg-slate-50/50" data-testid="sales-inbox-page">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="flex h-screen lg:h-screen bg-slate-50">
      {/* ── Left: message list ── */}
      <div className="w-[380px] border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Inbox className="w-5 h-5 text-indigo-600" /> Sales Inbox
            </h2>
            <Button size="sm" variant="outline" onClick={pollNow} disabled={polling}
              className="h-7 text-[11px]" data-testid="poll-now-btn">
              {polling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>
          </div>
          <div className="flex gap-1 flex-wrap">
            {['', 'external_lead', 'internal_instruction', 'internal_followup'].map(f => (
              <button key={f || 'all'}
                onClick={() => setClassFilter(f)}
                className={`px-2 py-0.5 text-[10px] rounded ${classFilter === f ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {f ? CLASS_STYLE[f]?.label : 'All'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-4 text-center text-slate-400 text-xs">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-6 text-center text-slate-400 text-xs">
              <Mail className="w-8 h-8 mx-auto mb-2 opacity-30" />
              No messages yet. Send an email to sales@voltide.energy.
            </div>
          )}
          {rows.map(r => (
            <button key={r.inbox_message_id}
              onClick={() => openDetail(r.inbox_message_id)}
              className={`w-full text-left p-3 border-b border-slate-100 hover:bg-indigo-50/40 transition-colors ${selectedId === r.inbox_message_id ? 'bg-indigo-50' : ''}`}
              data-testid={`inbox-row-${r.inbox_message_id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="text-xs font-semibold text-slate-900 truncate flex-1">{r.from_name || r.from_email}</span>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">{new Date(r.received_at).toLocaleDateString()}</span>
              </div>
              <p className="text-xs text-slate-700 truncate">{r.subject || '(no subject)'}</p>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                {r.classification && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${CLASS_STYLE[r.classification.type]?.bg || 'bg-slate-100'}`}>
                    {CLASS_STYLE[r.classification.type]?.label || r.classification.type}
                  </span>
                )}
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${STATUS_STYLE[r.status] || 'bg-slate-100'}`}>
                  {r.status}
                </span>
                {r.attachments?.length > 0 && (
                  <span className="text-[9px] text-slate-500 flex items-center gap-0.5">
                    <Paperclip className="w-2.5 h-2.5" />{r.attachments.length}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Right: detail + draft ── */}
      <div className="flex-1 overflow-y-auto">
        {!detail && (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm">
            Select a message
          </div>
        )}
        {detail && (
          <div className="p-6 space-y-4 max-w-4xl">
            {/* Header */}
            <div className="bg-white border border-slate-200 rounded p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900">{detail.message.subject || '(no subject)'}</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    <strong>{detail.message.from_name || ''}</strong> &lt;{detail.message.from_email}&gt; · {new Date(detail.message.received_at).toLocaleString()}
                  </p>
                </div>
                {detail.message.classification && (
                  <span className={`text-[10px] px-2 py-0.5 rounded ${CLASS_STYLE[detail.message.classification.type]?.bg}`}>
                    {CLASS_STYLE[detail.message.classification.type]?.label}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-3 max-h-80 overflow-y-auto">
                {detail.message.body_text || '(empty body)'}
              </div>

              {detail.message.attachments?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Attachments</p>
                  <div className="flex flex-wrap gap-2">
                    {detail.message.attachments.map((a, i) => (
                      <a key={a.storage_url || a.file_name || i} href={API.replace(/\/api$/, '') + a.storage_url}
                        target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs text-slate-700 hover:bg-slate-100">
                        <FileText className="w-3 h-3" /> {a.file_name}
                        <span className="text-[10px] text-slate-400">({Math.round((a.size_bytes || 0)/1024)} kB)</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Linked entities */}
            <div className="bg-white border border-slate-200 rounded p-4">
              <h4 className="text-xs font-bold text-slate-700 mb-2">Linked records</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <LinkRow label="Prospect" icon={<UserPlus className="w-3 h-3" />}
                  id={detail.message.linked_entities?.prospect_id} path="/prospects" />
                <LinkRow label="Site" icon={<Building2 className="w-3 h-3" />}
                  id={detail.message.linked_entities?.site_id} path="/prospects/sites" />
                <LinkRow label="Proposal" icon={<FileText className="w-3 h-3" />}
                  id={detail.message.linked_entities?.proposal_id} path="/proposals/v2" />
              </div>
            </div>

            {/* Draft reply */}
            {detail.draft && (
              <div className="bg-white border-2 border-indigo-200 rounded p-4 space-y-3" data-testid="draft-reply-editor">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-indigo-900 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Draft reply
                    {detail.draft.status === 'sent' && (
                      <span className="ml-2 bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Sent
                      </span>
                    )}
                    {detail.draft.status === 'failed' && (
                      <span className="ml-2 bg-red-100 text-red-800 text-[10px] px-2 py-0.5 rounded flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Failed
                      </span>
                    )}
                  </h4>
                  <span className="text-[10px] text-slate-400">To: {detail.draft.to_email}</span>
                </div>
                <Input
                  value={draftEdit.subject}
                  onChange={e => setDraftEdit(d => ({ ...d, subject: e.target.value }))}
                  disabled={detail.draft.status === 'sent'}
                  className="text-xs font-medium"
                  data-testid="draft-subject-input"
                />
                <Textarea
                  value={draftEdit.body_html}
                  onChange={e => setDraftEdit(d => ({ ...d, body_html: e.target.value }))}
                  disabled={detail.draft.status === 'sent'}
                  rows={10}
                  className="text-xs font-mono"
                  data-testid="draft-body-input"
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={saveDraft} disabled={detail.draft.status === 'sent'}
                    className="h-8 text-xs" data-testid="draft-save-btn">
                    Save draft
                  </Button>
                  <div className="flex-1" />
                  {detail.message.classification?.type === 'internal_instruction' && (
                    <Button size="sm" variant="outline" onClick={generateProposal} disabled={generating}
                      className="h-8 text-xs" data-testid="generate-proposal-btn">
                      {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      Generate Proposal Draft
                    </Button>
                  )}
                  <Button size="sm" onClick={sendReply}
                    disabled={sending || detail.draft.status === 'sent'}
                    className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
                    data-testid="send-reply-btn">
                    {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                    Send via Titan
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}

const LinkRow = ({ label, icon, id, path }) => (
  <div className="bg-slate-50 rounded p-2 flex items-center justify-between">
    <span className="text-slate-500 flex items-center gap-1">{icon} {label}</span>
    {id ? (
      <a href={`${path}/${id}`} className="text-indigo-600 hover:underline flex items-center gap-0.5 text-[11px] font-mono">
        {id.slice(0, 8)} <ExternalLink className="w-2.5 h-2.5" />
      </a>
    ) : <span className="text-slate-300 text-[10px]">—</span>}
  </div>
);
